import type { CanBeActive, INote } from "~/models/IUserEntry";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

function internalSetup(
  latestParagraph: MaybeRef<ILegalTextParagraph | undefined>,
  activeOldParagraph: MaybeRef<ILegalTextParagraph | undefined>,
  activeEntry: MaybeRef<CanBeActive | undefined>
) {
  const activeOldParagraphId = computed(() => toValue(activeOldParagraph)?.id);
  const { data: activeOldParagraphNotes } = useFetchParagraphEntryByType(
    activeOldParagraphId,
    "note"
  );

  const latestParagraphId = computed(() => toValue(latestParagraph)?.id);
  const { data: latestParagraphNotes } = useFetchParagraphEntryByType(
    latestParagraphId,
    "note"
  );

  const firstAncestorId = computed(() => {
    const ancestors = toValue(latestParagraph)?.ancestors;
    return ancestors?.[ancestors.length - 1]?.id;
  });

  const { data: firstAncestorNotes } = useFetchParagraphEntryByType(
    firstAncestorId,
    "note"
  );

  const notes = computed(() => [
    ...(latestParagraphNotes.value ?? []),
    ...(firstAncestorNotes.value ?? []),
  ]);

  const sortedNotes = useSortEntries(notes);

  // notes of the "active entry" note's version
  const activeNoteVersionNotes = computed(() =>
    sortedNotes.value.filter(
      (item: INote) =>
        item.legal_text_paragraph.valid_until ===
        toValue(activeEntry)?.legal_text_paragraph.valid_until
    )
  );

  const currentParagraphNotes = computed(() =>
    activeOldParagraphId.value
      ? activeOldParagraphNotes.value
      : latestParagraphNotes.value
  );

  return {
    // latest law version & first-ancestor law version notes
    sortedNotes,
    // notes from the law version that is open now
    currentParagraphNotes,
    // notes of the "active entry" note's version
    activeNoteVersionNotes,
  };
}

const paragraphNotesStore = defineStore("notes", () => {
  provideQueries();
  const {
    query: { data: currentParagraph },
  } = useCurrentParagraph();
  const { active } = useFetchActiveEntry();
  const noteIsActive = computed(() => active.value?.type === "note");
  const { oldParagraph } = useOldParagraphState();
  const {
    query: { data: notesActiveParagraph },
  } = useFetchParagraphById(
    toRef(() =>
      noteIsActive.value ? active.value?.legal_text_paragraph_id : 0
    )
  );

  const activeParagraph = computed(
    () => notesActiveParagraph.value ?? currentParagraph.value
  );

  return {
    ...internalSetup(activeParagraph, oldParagraph, active),
    activeParagraph,
  };
});

export function useParagraphNotes() {
  return storeToRefs(paragraphNotesStore());
}
